import React, { useState } from "react";
import { Button, Select, Badge, Modal } from "../../components/ui";
import { useToast } from "../../context/ToastContext";

const GroupInfoModal = ({
  isOpen,
  onClose,
  conversation,
  availableUsers = [],
  onAddMember,
  onRemoveMember,
  currentUserId,
}) => {
  const [selectedUserId, setSelectedUserId] = useState("");
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  if (!isOpen || !conversation) return null;

  const activeMembers = conversation.participants?.filter((p) => p.isActive) || [];

  const handleAdd = async () => {
    if (!selectedUserId) return;
    const targetUser = availableUsers.find((u) => u._id === selectedUserId);
    if (!targetUser) return;

    setLoading(true);
    try {
      await onAddMember(conversation._id, {
        userId: targetUser._id,
        userModel: targetUser.userModel || "Staff",
      });
      setSelectedUserId("");
    } catch (err) {
      toast.error("Failed to add member");
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (userId) => {
    if (!window.confirm("Remove this member from the group?")) return;
    setLoading(true);
    try {
      await onRemoveMember(conversation._id, userId);
    } catch (err) {
      toast.error("Failed to remove member");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Group Info"
      size="md"
    >
      <div className="space-y-5">
        

        <div>
          <h3 className="text-sm font-bold text-neutral-900 dark:text-neutral-100">{conversation.name}</h3>
          {conversation.description && (
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">{conversation.description}</p>
          )}
        </div>

        {/* Add Member Section */}
        <div className="space-y-2 pt-2 border-t border-neutral-100 dark:border-neutral-800">
          <label className="block text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
            Add New Member
          </label>
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <Select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                className="text-xs"
              >
                <option value="">Select a user...</option>
                {availableUsers.map((u) => (
                  <option key={u._id} value={u._id}>
                    {u.name} ({u.userModel})
                  </option>
                ))}
              </Select>
            </div>
            <Button
              variant="primary"
              size="sm"
              onClick={handleAdd}
              isLoading={loading}
              disabled={!selectedUserId}
              className="text-xs font-semibold"
            >
              Add
            </Button>
          </div>
        </div>

        {/* Members List */}
        <div className="space-y-2 pt-2 border-t border-neutral-100 dark:border-neutral-800">
          <label className="block text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
            Members ({activeMembers.length})
          </label>
          <div className="max-h-48 overflow-y-auto border border-neutral-200/60 rounded-xl divide-y divide-neutral-100 dark:divide-neutral-800">
            {activeMembers.map((member) => {
              const isSelf = member.userId?._id?.toString() === currentUserId?.toString();
              return (
                <div
                  key={member.userId?._id || member._id}
                  className="flex items-center justify-between p-3 text-xs"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-neutral-800 dark:text-neutral-100">
                      {member.userId?.name || "Member"}
                    </span>
                    {isSelf && <span className="text-[11px] text-neutral-400 dark:text-neutral-500 font-medium">(You)</span>}
                    {member.role === "ADMIN" && (
                      <Badge variant="primary" className="text-[10px] font-bold px-1.5 py-0.2">
                        ADMIN
                      </Badge>
                    )}
                  </div>
                  {!isSelf && (
                    <button
                      type="button"
                      onClick={() => handleRemove(member.userId?._id)}
                      disabled={loading}
                      className="text-xs text-rose-600 hover:text-rose-700 font-medium transition"
                    >
                      Remove
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex justify-end pt-3 border-t border-neutral-100 dark:border-neutral-800">
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
          >
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default GroupInfoModal;